import { useEffect, useRef } from "react";

import { useComposicao } from "../../lib/projecao/hooks";
import type {
  ComposicaoOut,
  ItemComposicao,
  ReceitaAtrasada,
} from "../../lib/projecao/tipos";
import { formatarDataISO } from "../../lib/formato/data";
import { formatarDecimal } from "../../lib/formato/decimal";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Modal de drill-down itemizado de um dia da projeção (FE-8 — Tela 2 §10.5).
 *
 * Consome o BE gap A (`GET /api/empresas/{id}/projecao/composicao`) via
 * `useComposicao`. Abre quando o operador clica num dia da `TabelaProjecao` na
 * visão EMPRESA (a visão GRUPO mantém o drill-down raso — sem este modal).
 *
 * Estrutura de modal espelha `ModalReagendar`/`ConfirmacaoConciliacao`:
 * `role="dialog"` + `aria-modal`, rotulado por `aria-labelledby`, foco movido ao
 * abrir (botão Fechar) e Escape fecha.
 *
 * Money via `formatarDecimal` (STRING-based, sem "R$", NUNCA `Number`); datas via
 * `formatarDataISO`. A composição é só LEITURA: nenhuma mutação, nenhum gate de
 * permissão extra (quem vê a projeção da empresa já tem `VISUALIZAR_PROJECAO`; o
 * backend 404a quem não tem).
 *
 * As `receitas_atrasadas_nao_somadas` ficam num bloco visualmente DISTINTO com a
 * nota de que NÃO entram na soma do dia — são apenas D+0, para transparência.
 */

const SEM_VALOR = "—";

/** Rótulos pt-BR do `tipo` do item. */
const ROTULO_TIPO: Record<ItemComposicao["tipo"], string> = {
  obrigacao: "Obrigação",
  receita: "Receita",
};

/** Rótulos pt-BR do `status` do item. */
const ROTULO_STATUS: Record<ItemComposicao["status"], string> = {
  regular: "Regular",
  vencida: "Vencida",
  pendente_erp: "Pendente no ERP",
};

/** Classe de cor por `status` — `vencida`/`pendente_erp` em destaque de aviso. */
const COR_STATUS: Record<ItemComposicao["status"], string> = {
  regular: "",
  vencida: "text-aviso",
  pendente_erp: "text-aviso",
};

interface ModalComposicaoDiaProps {
  empresaId: string;
  /** Dia selecionado (ISO `YYYY-MM-DD`) cuja composição o modal exibe. */
  data: string;
  horizonte: number;
  /** Fecha o modal (Escape, botão Fechar). */
  aoFechar: () => void;
}

export function ModalComposicaoDia({
  empresaId,
  data,
  horizonte,
  aoFechar,
}: ModalComposicaoDiaProps): JSX.Element {
  const consulta = useComposicao(empresaId, data, horizonte);
  const fecharRef = useRef<HTMLButtonElement>(null);

  // Move o foco para o botão Fechar ao abrir (acessibilidade de modal).
  useEffect(() => {
    fecharRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="titulo-composicao-dia"
      onKeyDown={(evento) => {
        if (evento.key === "Escape") {
          aoFechar();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-off-black/50 p-4"
    >
      <div className="flex max-w-2xl flex-col gap-4 rounded-lg border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
        <h2
          id="titulo-composicao-dia"
          className="font-titulo text-xl text-off-black"
        >
          Composição do dia
        </h2>

        {consulta.isPending ? (
          <p className="font-corpo text-base text-off-black">
            Carregando composição…
          </p>
        ) : consulta.isError ? (
          <p role="alert" className="font-corpo text-base text-flame">
            {mensagemErro(consulta.error)}
          </p>
        ) : (
          <ConteudoComposicao composicao={consulta.data} />
        )}

        <div className="flex justify-end">
          <button
            ref={fecharRef}
            type="button"
            onClick={aoFechar}
            className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

interface ConteudoComposicaoProps {
  composicao: ComposicaoOut;
}

/** Corpo do modal quando a composição carregou: cabeçalho + tabelas. */
function ConteudoComposicao({
  composicao,
}: ConteudoComposicaoProps): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="font-subtitulo">
          {formatarDataISO(composicao.data)} · {composicao.dia_semana}
        </p>
        <p>
          Saídas: {formatarDecimal(composicao.saldo_saidas)} · Entradas:{" "}
          {formatarDecimal(composicao.saldo_entradas)}
        </p>
      </div>

      <TabelaItens itens={composicao.itens} />

      {composicao.receitas_atrasadas_nao_somadas.length > 0 ? (
        <SecaoReceitasAtrasadas
          receitas={composicao.receitas_atrasadas_nao_somadas}
        />
      ) : null}
    </div>
  );
}

interface TabelaItensProps {
  itens: ItemComposicao[];
}

/** Tabela dos itens que SOMAM no dia (obrigações + receitas). */
function TabelaItens({ itens }: TabelaItensProps): JSX.Element {
  if (itens.length === 0) {
    return (
      <p className="font-corpo text-base text-off-black">Sem itens neste dia.</p>
    );
  }

  return (
    <table className="w-full border-collapse font-corpo text-base text-off-black">
      <thead>
        <tr className="border-b border-platinum text-left">
          <th className="px-2 py-1 font-subtitulo">Data prevista</th>
          <th className="px-2 py-1 font-subtitulo">Descrição</th>
          <th className="px-2 py-1 font-subtitulo">Tipo</th>
          <th className="px-2 py-1 font-subtitulo">Status</th>
          <th className="px-2 py-1 font-subtitulo">Dias atraso</th>
          <th className="px-2 py-1 text-right font-subtitulo">Valor</th>
        </tr>
      </thead>
      <tbody>
        {itens.map((item) => (
          <tr key={item.id} className="border-b border-platinum">
            <td className="px-2 py-1">
              {formatarDataISO(item.data_prevista)}
            </td>
            <td className="px-2 py-1">{item.descricao ?? SEM_VALOR}</td>
            <td className="px-2 py-1">{ROTULO_TIPO[item.tipo]}</td>
            <td className={`px-2 py-1 ${COR_STATUS[item.status]}`}>
              {ROTULO_STATUS[item.status]}
            </td>
            <td className="px-2 py-1">{item.dias_atraso ?? SEM_VALOR}</td>
            <td className="px-2 py-1 text-right tabular-nums">
              {formatarDecimal(item.valor)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface SecaoReceitasAtrasadasProps {
  receitas: ReceitaAtrasada[];
}

/**
 * Bloco visualmente distinto das receitas atrasadas que NÃO somam no dia (só D+0,
 * transparência). A nota deixa explícito que não entram na soma.
 */
function SecaoReceitasAtrasadas({
  receitas,
}: SecaoReceitasAtrasadasProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2 rounded-md bg-platinum p-4">
      <p className="font-subtitulo">
        Receitas atrasadas — não entram na soma do dia.
      </p>
      <table className="w-full border-collapse font-corpo text-base text-off-black">
        <thead>
          <tr className="border-b border-off-black/20 text-left">
            <th className="px-2 py-1 font-subtitulo">Descrição</th>
            <th className="px-2 py-1 font-subtitulo">Data prevista</th>
            <th className="px-2 py-1 font-subtitulo">Dias atraso</th>
            <th className="px-2 py-1 text-right font-subtitulo">Valor</th>
          </tr>
        </thead>
        <tbody>
          {receitas.map((receita) => (
            <tr key={receita.id} className="border-b border-off-black/20">
              <td className="px-2 py-1">{receita.descricao ?? SEM_VALOR}</td>
              <td className="px-2 py-1">
                {formatarDataISO(receita.data_prevista)}
              </td>
              <td className="px-2 py-1">{receita.dias_atraso}</td>
              <td className="px-2 py-1 text-right tabular-nums">
                {formatarDecimal(receita.valor)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Traduz o erro da consulta numa mensagem estável (mesma semântica da página
 * Projecao). 503 → indisponível; demais `ErroHttp` reusam a mensagem; erro
 * não-`ErroHttp` → fallback genérico.
 */
function mensagemErro(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.moduloIndisponivel) {
      return "Módulo de projeção indisponível no momento.";
    }
    return erro.message;
  }
  return "Não foi possível carregar a composição.";
}
